import { SiteFooter } from "@/components/SiteFooter";
import { InstallTip } from "@/components/InstallTip";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import api from "@/lib/api";
import { Activity, ChevronDown, CreditCard, FileText, LayoutDashboard, ListOrdered, ShieldCheck, Ticket, Trophy, LogOut, ScanLine, SlidersHorizontal, FlaskConical } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/program", label: "Race Program", icon: ListOrdered, id: "nav-program-link" },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard-link" },
  { to: "/results", label: "Winners & Places", icon: Trophy, id: "nav-results-link" },
  { to: "/sheets", label: "Final Sheets", icon: FileText, id: "nav-sheets-link", admin: true },
  { to: "/import", label: "Import / Scan", icon: ScanLine, id: "nav-import-link", admin: true },
  { to: "/settings", label: "Rules & Matrices", icon: SlidersHorizontal, id: "nav-settings-link", admin: true },
  { to: "/subscribe", label: "Subscription", icon: CreditCard, id: "nav-subscribe-link" },
  { to: "/promos", label: "Promo Codes", icon: Ticket, id: "nav-promos-link", admin: true },
  { to: "/matrix-impact", label: "Matrix Impact", icon: FlaskConical, id: "nav-impact-link", admin: true },
];

const AdminMenu = ({ items, testId = "nav-admin-menu" }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const active = items.some((l) => pathname.startsWith(l.to));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid={testId} title="Admin"
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-sm whitespace-nowrap transition-colors outline-none ${active ? "bg-slate-800 text-white" : "text-amber-300/90 hover:text-white hover:bg-slate-800/50"}`}>
        <ShieldCheck className="h-4 w-4" /><span className="hidden lg:inline">Admin</span><ChevronDown className="h-3.5 w-3.5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="bg-[#0F172A] border-slate-700 text-slate-200 min-w-[200px]">
        <DropdownMenuLabel className="eyebrow">Admin only</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-slate-800" />
        {items.map(({ to, label, icon: Icon, id }) => (
          <DropdownMenuItem key={to} onSelect={() => navigate(to)} data-testid={id}
            className={`cursor-pointer gap-2 ${pathname.startsWith(to) ? "text-emerald-300" : ""}`}>
            <Icon className="h-4 w-4" />{label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));

const PlanBadge = () => {
  const { pathname } = useLocation();
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/billing/me").then((r) => setB(r.data)).catch(() => {}); }, [pathname]);
  if (!b) return null;
  const text = b.admin ? "Admin" : b.in_trial ? `Trial · ${daysLeft(b.trial_ends_at)}d left`
    : b.active ? `Pass · ${daysLeft(b.access_until)}d left` : "Expired";
  const tone = b.active ? (b.in_trial ? "text-amber-300 border-amber-500/40" : "text-emerald-300 border-emerald-500/40") : "text-rose-300 border-rose-500/40";
  return <NavLink to="/subscribe" data-testid="nav-plan-status" className={`text-[11px] font-mono px-2 py-0.5 rounded border whitespace-nowrap ${tone}`}>{text}</NavLink>;
};

const MobileNav = ({ items, admin }) => (
  <nav className="md:hidden flex items-center justify-around border-t border-slate-800 px-1 h-11" data-testid="mobile-nav">
    {items.map(({ to, label, icon: Icon, id }) => (
      <NavLink key={to} to={to} title={label} aria-label={label} data-testid={`${id}-mobile`}
        className={({ isActive }) => `p-2 rounded transition-colors ${isActive ? "bg-slate-800 text-emerald-300" : "text-slate-400 hover:text-white"}`}>
        <Icon className="h-5 w-5" />
      </NavLink>
    ))}
    {admin.length > 0 && <AdminMenu testId="nav-admin-menu-mobile" items={admin.map((l) => ({ ...l, id: `${l.id}-mobile` }))} />}
  </nav>
);

export const AppShell = () => {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const items = links.filter((l) => !l.admin);
  const adminItems = isAdmin ? links.filter((l) => l.admin) : [];
  return (
    <div className="min-h-screen grid-bg noise">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#080C14]/85 border-b border-slate-800">
        <div className="max-w-[1760px] mx-auto px-3 md:px-8 h-14 flex items-center gap-2 sm:gap-4">
          <NavLink to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo" title="Home page">
            <span className="h-7 w-7 grid place-items-center rounded bg-emerald-500 text-[#080C14]">
              <Activity className="h-4 w-4" strokeWidth={2.5} />
            </span>
            <span className="font-display text-xl font-bold tracking-wide">
              STRATEGIC<span className="text-emerald-400">/</span>RATINGS
            </span>
          </NavLink>
          <nav className="hidden md:flex items-center gap-1 overflow-x-auto min-w-0">
            {items.map(({ to, label, icon: Icon, id }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={id}
                title={label}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-2.5 py-1.5 rounded text-sm whitespace-nowrap transition-colors duration-150 ${
                    isActive ? "bg-slate-800 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800/50"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                <span className="hidden xl:inline">{label}</span>
              </NavLink>
            ))}
            {adminItems.length > 0 && <AdminMenu items={adminItems} />}
          </nav>
          <div className="ml-auto flex items-center gap-2 sm:gap-3 shrink-0">
            <PlanBadge />
            <span className="hidden 2xl:inline text-xs num text-slate-500 max-w-[180px] truncate" data-testid="nav-user-email">{user?.email}</span>
            <button
              onClick={logout}
              data-testid="nav-logout-button"
              className="p-2 rounded text-slate-400 hover:text-rose-400 hover:bg-slate-800/60 transition-colors"
              title={`Log out ${user?.email || ""}`}
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
        <MobileNav items={items} admin={adminItems} />
      </header>
      <main className="max-w-[1500px] mx-auto px-4 md:px-8 py-6 md:py-10">
        <Outlet />
      </main>
      <SiteFooter />
      {user?.role !== "admin" && <InstallTip />}
    </div>
  );
};
